import React, { useEffect, useState } from 'react';
import PageText from 'components/base/page-text';
import useDashboardContext from 'contexts/dashboard.context';
import { BrandType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import SearchNav from 'components/search-nav';
import DeleteModal from 'components/base/delete-modal';
import BrandCard from 'components/brand-card';
import { BrandCardOptionType } from 'components/brand-card/brand-card-options';

const TrashedBrands = () => {
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [hasError, setHasError] = useState(false);
  const [showRestoreBrandModal, setShowRestoreBrandModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [activeBrand, setActiveBrand] = useState<BrandType | null>(null);
  const [selectedBrands, setSelectedBrands] = useState([] as string[]);

  const { activeCokitchen } = useDashboardContext();
  const { category } = useDashboardContext();

  const fetchData = async () => {
    if (!category) return;
    try {
      setLoading(true);
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const sortByArchived = res.data
        ?.filter((b: BrandType) => b.item_type === category.toUpperCase() && b.archived)
        ?.sort((a: BrandType, b: BrandType) => b.updated_at.localeCompare(a.updated_at));
      setBrands(sortByArchived);
    } catch {
      setBrands([]);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [category, activeCokitchen]);

  const handleSelectAll = () => {
    const allIds = brands?.map((item: any) => item.id);
    setSelectedBrands(allIds);
  };

  const handleChange = (e: any, id: string) => {
    if (e.target.checked) {
      setSelectedBrands([...selectedBrands, id]);
    } else {
      setSelectedBrands((prev: any) => prev.filter((item: string) => item !== id));
    }
  };
  const restoreClick = async (item?: BrandType | null) => {
    try {
      const payload = {
        brand_ids: item?.id ? [item.id] : selectedBrands,
        archived: false
      };
      const res = await apiService.post(`brand_management/multiple-brand/archived`, {
        data: payload
      });

      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`${selectedBrands.length > 1 ? 'Brands' : 'Brand'} restore successfully`}
          />
        );
        setDeleteLoading(false);
        setShowRestoreBrandModal(false);
        setSelectedBrands([]);
        fetchData();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };
  const handleBrandOption = (option: BrandCardOptionType, brand: BrandType) => {
    switch (option) {
      case 'restore':
        setShowRestoreBrandModal(!showRestoreBrandModal);
        break;

      case 'select':
        setActiveBrand(brand);
        break;

      default:
        break;
    }
  };

  return (
    <div>
      {loading ? (
        <PageText text='Loading...' />
      ) : brands.length > 0 ? (
        <div className='grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'>
          {brands?.map((brand) => (
            <BrandCard
              key={brand.id}
              data={brand}
              options={['restore', 'select']}
              isChecked={selectedBrands.includes(brand.id)}
              onCheck={(e) => handleChange(e, brand.id)}
              onClick={(option) => handleBrandOption(option, brand)}
            />
          ))}
        </div>
      ) : (
        <PageText
          text={hasError ? 'Error fetching brands list. Contact tech support' : 'No brands to show'}
        />
      )}

      {selectedBrands.length > 0 && (
        <SearchNav
          count={selectedBrands.length}
          handleSelectAll={handleSelectAll}
          onDoneClick={() => setSelectedBrands([])}
          onRestoreClick={restoreClick}
        />
      )}
      <DeleteModal
        onClose={() => setShowRestoreBrandModal(false)}
        show={showRestoreBrandModal}
        description={`Are you sure you want to restore ${
          selectedBrands.length > 1 ? 'brands' : activeBrand?.name
        }`}
        handleDelete={() => restoreClick(activeBrand)}
        loading={deleteLoading}
        title={`Restore  ${
          selectedBrands.length > 1 ? `${selectedBrands.length} brands` : activeBrand?.name
        }?`}
        type='restore'
      />
    </div>
  );
};

export default TrashedBrands;
